import { Component, effect, inject, signal } from '@angular/core'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatDialog } from '@angular/material/dialog'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { MatTooltipModule } from '@angular/material/tooltip'
import { HttpService } from '@bases/http.service'
import { LayerUploaded } from '@core/models/layers.model'
import { UploadedLayersService } from '@core/services/layers-uploaded.service'
import { LayersService } from '@core/services/layers.service'
import { SessionManagerService } from '@core/services/session-manager.service'
import { CardLayerPersonalComponent } from './card-layer-personal/card-layer-personal.component'
import { DialogPersonalLayersComponent } from './dialog-personal-layers/dialog-personal-layers.component'

@Component({
  selector: 'app-personal-layers',
  standalone: true,
  templateUrl: './personal-layers.component.html',
  providers: [HttpService],
  imports: [
    // Material
    MatTooltipModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatFormFieldModule,
    MatSelectModule,
    MatAutocompleteModule,
    // Components
    CardLayerPersonalComponent,
  ],
})
export class PersonalLayersComponent {
  private readonly _dialog = inject(MatDialog)
  private readonly _uploadedLayersService = inject(UploadedLayersService)
  private readonly _sessionManagerService = inject(SessionManagerService)
  layersService = inject(LayersService)

  readonly selectedFileToUpdate$ = signal<LayerUploaded | null>(null)
  readonly personalExpanded$ = signal(false)
  search$ = this.layersService.search$
  personalLayersState$ = this._uploadedLayersService.personalLayersState$
  personalLayers$ = this._uploadedLayersService.personalLayers$
  filteredPersonalLayers$ = this._uploadedLayersService.filteredPersonalLayers$
  stillSpaceOnAccountState$ = this._uploadedLayersService.stillSpaceOnAccountState$
  sortSelected$ = this._uploadedLayersService.sortSelectedPersonalLayer$

  constructor() {
    // Open panel if layer selected by snapshot
    effect(() => {
      if (this.personalLayers$().some((layer: LayerUploaded) => layer.isSelected)) {
        this.personalExpanded$.set(true)
      }
    })

    // Expand when search returns personal results
    effect(() => {
      if (this.search$() && this.filteredPersonalLayers$().length > 0) {
        this.personalExpanded$.set(true)
      }
    })
  }

  async ngOnInit() {
    this._sessionManagerService.sizeAccount()
  }

  addPersonalLayer(event: Event): void {
    event.stopPropagation()

    const dialogRef = this._dialog.open(DialogPersonalLayersComponent, {
      minWidth: '50vw',
      disableClose: true,
      data: '',
    })

    dialogRef.afterClosed().subscribe(async (newLayer: LayerUploaded | null) => {
      if (newLayer) {
        this.personalExpanded$.set(true)
      }
      await this._uploadedLayersService.list()
      this._uploadedLayersService.sortUploadedLayers('personal', this.sortSelected$())
      this._sessionManagerService.sizeAccount()
    })
  }

  onPanelOpened() {
    this.personalExpanded$.set(true)
  }

  onPanelClosed() {
    this.personalExpanded$.set(false)
  }

  filterPersonalLayer(order: string): void {
    this._uploadedLayersService.sortUploadedLayers('personal', order)
  }
}
